import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { QuestionNotificationResponse } from '../types/model/stage/QuestionNotificationResponse';

export interface NotificationState {
  notifications: QuestionNotificationResponse[];
  unreadCount: number;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
};

const getReadNotificationsFromStorage = (): number[] => {
  try {
    const readIds = localStorage.getItem('read_notifications');
    return readIds ? JSON.parse(readIds) : [];
  } catch (error) {
    console.error('Error reading read_notifications from localStorage', error);
    return [];
  }
};

const setReadNotificationsInStorage = (readIds: number[]) => {
  try {
    localStorage.setItem('read_notifications', JSON.stringify(readIds));
  } catch (error) {
    console.error('Error writing read_notifications to localStorage', error);
  }
};

const getDeletedNotificationsFromStorage = (): number[] => {
  try {
    const deletedIds = localStorage.getItem('deleted_notifications');
    return deletedIds ? JSON.parse(deletedIds) : [];
  } catch (error) {
    console.error('Error reading deleted_notifications from localStorage', error);
    return [];
  }
};

const addDeletedNotificationToStorage = (id: number) => {
  try {
    const deletedIds = getDeletedNotificationsFromStorage();
    if (!deletedIds.includes(id)) {
      localStorage.setItem('deleted_notifications', JSON.stringify([...deletedIds, id]));
    }
  } catch (error) {
    console.error('Error writing deleted_notifications to localStorage', error);
  }
};


const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setNotifications(state, action: PayloadAction<QuestionNotificationResponse[]>) {
      const readIds = getReadNotificationsFromStorage();
      const deletedIds = getDeletedNotificationsFromStorage();

      const validNotifications = action.payload
        .filter(n => !deletedIds.includes(n.id))
        .map(n => ({
          ...n,
          read: n.read || readIds.includes(n.id),
        }));

      state.notifications = validNotifications;
      state.unreadCount = validNotifications.filter(n => !n.read).length;
    },
    markAsRead(state, action: PayloadAction<number>) {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount--;
        const readIds = getReadNotificationsFromStorage();
        if (!readIds.includes(action.payload)) {
          setReadNotificationsInStorage([...readIds, action.payload]);
        }
      }
    },
    removeNotification(state, action: PayloadAction<number>) {
      const notificationId = action.payload;
      
      addDeletedNotificationToStorage(notificationId);

      state.notifications = state.notifications.filter(n => n.id !== notificationId);
      state.unreadCount = state.notifications.filter(n => !n.read).length;
      
      const readIds = getReadNotificationsFromStorage();
      if (readIds.includes(notificationId)) {
        const newReadIds = readIds.filter(id => id !== notificationId);
        setReadNotificationsInStorage(newReadIds);
      }
    },
    clearNotifications(state) {
      state.notifications = [];
      state.unreadCount = 0;
      localStorage.removeItem('read_notifications');
      localStorage.removeItem('deleted_notifications');
    },
  },
});

export const { setNotifications, markAsRead, removeNotification, clearNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;
